import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { JobType } from "../../../../../../../models/jobType";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import OrderTypeModal from "./OrderTypeModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Row = {
  id?: number;
  status?: string;
};

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const OrderTypePage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    jobTypes,
    hasErrors,
    page,
    total,
    pageSize,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createJobType,
    updateJobType,
  } = useActions();

  const [selectedJobType, setSelectedJobType] = useState<JobType>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const editJobType = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findJobType = jobTypes.find((jobType) => jobType.id === index);

    if (findJobType) {
      setSelectedJobType(findJobType);
      setOpenModal(true);
    }
  };

  const changeStatusJobType = (index: number) => {
    const findJobType = jobTypes.find((jobType) => jobType.id === index);

    if (findJobType) {
      updateJobType({
        id: Number(index),
        jobType: {
          ...findJobType,
          status: findJobType.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          data-cy="changeStatus"
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveJobTypeSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, jobTypes);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "name", title: translate("orderType") },
    {
      name: "description",
      title: translate("description"),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setSelectedJobType(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("orderType"),
          column: "name",
        },
        {
          name: translate("description"),
          column: "description",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.job_type",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="jobTypeDataList">
      <ShipeeziNewGrid
        data={jobTypes}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (jobTypeId) => editJobType(Number(jobTypeId))
            : undefined,
          handleChange: (jobTypeId) => changeStatusJobType(Number(jobTypeId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveJobTypeSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <OrderTypeModal
          jobType={selectedJobType}
          open={openModal}
          hasError={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          updateOrCreateJobType={(jobType: JobType) =>
            selectedJobType
              ? selectedJobType.id &&
                updateJobType({
                  id: selectedJobType.id,
                  jobType: { ...selectedJobType, ...jobType },
                })
              : createJobType({
                  ...jobType,
                  companyId: currentUser?.companyId,
                  valid: true,
                })
          }
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateJobType({ id: rowId, jobType: { status } as JobType });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default OrderTypePage;
